/**
 * The sign-up moment — Blockari's `shell/SignUpSheet.tsx`, out of React: a slim
 * bottom sheet on a phone, a centred card on a desktop. What is at stake is
 * SHOWN, not explained — the locked chest (its own 3D still, a padlock and a
 * count), the rank badge, the name — over the two fields the account needs and
 * one big CTA. A title and the button; "Later" is one tap, and so is the scrim
 * and Escape. Nothing here blocks: it is only ever on screen because the
 * player asked, or on a calm screen once a day (signup-state.ts).
 *
 * Also here: the celebration (the padlock comes off in a burst of light and
 * coins, then the chests arrive) and the ambient prompt's scheduler.
 */

import type { Rarity } from '@/game/chests';
import { guestValueOf, login, register, suggestName, type Account, type ChestSummary, type GuestValue } from '@/net/api';
import { playChestBeat } from '@/ceremony/sound';
import { el } from '@/ui/hud';
import { coin, hueOf, initialsOf, rankBadgeCard } from '@/ui/screens';

import { arrivalStore } from './arrival-state';
import { chestStore } from './chests-state';
import { chestIcon, lockIcon, unlockIcon } from './glyphs';
import { chestStill } from './chest-still';
import {
  CELEBRATE_MS,
  PROMPT_DELAY_MS,
  URGENT_DAYS,
  afterRegistration,
  afterSignIn,
  arriveAfterCelebration,
  bestRarity,
  canPrompt,
  daysLeft,
  dismissSignup,
  finishCelebration,
  guestLossParts,
  keepNameOffer,
  listWords,
  openSignup,
  registerInstead,
  promptDue,
  switchSheet,
  signupStore,
  viewerStore,
  type SignupReason,
} from './signup-state';

const TITLE: Record<SignupReason, (chests: number) => string> = {
  chest: (n) => (n > 1 ? 'Claim your chests' : 'Claim your chest'),
  rank: () => 'Keep your rank',
  board: () => 'Claim your name',
  keep: () => 'Keep this guest',
  prompt: (n) => (n > 1 ? 'Claim your chests' : n === 1 ? 'Claim your chest' : 'Keep your rank'),
};

const reducedMotion = (): boolean =>
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

function errorCopy(error: string): string {
  if (error === 'username-taken') return 'That name is taken. Try another.';
  if (error === 'bad-request') return 'Username: 3–20 letters, digits or underscores. Password: at least 8 characters.';
  return 'Could not create the account. Try again.';
}

function signInErrorCopy(error: string): string {
  if (error === 'bad-credentials') return 'Wrong username or password.';
  if (error === 'bad-request') return 'Enter your username and password.';
  return 'Could not sign in. Check your connection and try again.';
}

/** The locked chest: its still (or the flat glyph until it is baked / without WebGL), a padlock and a count. */
function lockedChest(opts: { rarity: Rarity; count: number; days: number | null; small?: boolean; open?: boolean }): HTMLElement {
  const root = el('div', `w21-su-chest r-${opts.rarity}${opts.open ? ' unlocked' : ''}${opts.small ? ' small' : ''}`);
  root.append(el('i', 'w21-su-chest-glow'));
  const flat = el('span', 'w21-su-chest-flat');
  flat.setAttribute('aria-hidden', 'true');
  flat.append(chestIcon());
  root.append(flat);
  void chestStill(opts.rarity, 168).then((url) => {
    if (!url || !root.isConnected) return;
    const img = el('img', 'w21-su-chest-img') as HTMLImageElement;
    img.src = url;
    img.alt = '';
    img.draggable = false;
    flat.replaceWith(img);
  });
  const lock = el('span', 'w21-su-lock');
  lock.setAttribute('aria-hidden', 'true');
  lock.append(opts.open ? unlockIcon() : lockIcon());
  root.append(lock);
  if (opts.count > 1) root.append(el('b', 'w21-su-count', `×${opts.count}`));
  if (opts.days !== null && opts.days <= URGENT_DAYS && !opts.open) {
    const chip = el('span', 'w21-chip w21-amber w21-su-days', opts.days <= 0 ? 'Today' : opts.days === 1 ? '1 day' : `${opts.days} days`);
    root.append(chip);
  }
  return root;
}

/** What the player stands to keep. */
function stake(): { chests: ChestSummary[]; rarity: Rarity; days: number | null } {
  const s = chestStore.getState();
  const chests = [...s.held, ...s.unopened];
  return { chests, rarity: bestRarity(chests), days: daysLeft(chests) };
}

function avatar(name: string): HTMLElement {
  const a = el('span', 'w21-avatar');
  a.style.setProperty('--hue', String(hueOf(name)));
  a.textContent = initialsOf(name);
  a.setAttribute('aria-hidden', 'true');
  return a;
}

let sheet: HTMLElement | null = null;
let sheetMode: 'signup' | 'signin' | 'signout' = 'signup';
let sheetOff: (() => void) | null = null;
let celebration: HTMLElement | null = null;

function closeSheet(): void {
  sheetOff?.();
  sheetOff = null;
  sheet?.remove();
  sheet = null;
}

/** The sheet's foot: "Later" (one tap, always) and the way over to the other face. */
function footRow(swapLabel: string, onSwap: () => void): [HTMLElement, HTMLButtonElement] {
  const foot = el('div', 'w21-su-foot');
  const later = el('button', 'w21-su-later', 'Later') as HTMLButtonElement;
  later.type = 'button';
  later.addEventListener('click', () => dismissSignup());
  const swap = el('button', 'w21-su-later w21-su-swap', swapLabel) as HTMLButtonElement;
  swap.type = 'button';
  swap.addEventListener('click', onSwap);
  foot.append(later, swap);
  return [foot, later];
}

/** The layer both faces sit in: a scrim that dismisses, fitted to the visual viewport (keyboard up). */
function buildLayer(): HTMLElement {
  const layer = el('div', 'w21-su-layer');
  layer.addEventListener('click', (e) => {
    if (e.target === layer) dismissSignup();
  });
  // With a phone's keyboard up the VISUAL viewport is what is left of the screen (iOS never resizes
  // the layout one; Android Chrome does not by default either): fit the layer to it, so the sheet
  // sits above the keyboard, shrinks to fit and scrolls inside itself.
  const vv = globalThis.visualViewport;
  if (vv) {
    const fit = (): void => {
      layer.style.top = `${vv.offsetTop}px`;
      layer.style.height = `${vv.height}px`;
      layer.style.bottom = 'auto';
      // The keyboard has (finished) risen with a field focused: bring the foot up with it.
      const focused = document.activeElement;
      if (focused instanceof HTMLInputElement && layer.contains(focused)) {
        layer.querySelector('.w21-su-later')?.scrollIntoView({ block: 'end', behavior: 'smooth' });
      }
    };
    vv.addEventListener('resize', fit);
    vv.addEventListener('scroll', fit);
    fit();
    sheetOff = () => {
      vv.removeEventListener('resize', fit);
      vv.removeEventListener('scroll', fit);
    };
  }
  return layer;
}

/**
 * The question a guest with something to keep is asked before it is thrown away: by logging in to an account, or
 * by signing out. The amounts, then the way that keeps them (primary: register this guest), then the way that does
 * not (quiet), then Back. `leave` does the thing (true once done); `back` returns to where the question came from.
 */
function leaveFace(o: {
  verb: 'Log in' | 'Sign out';
  working: string;
  value: GuestValue;
  leave: () => Promise<boolean>;
  back: () => void;
  grab: HTMLElement;
}): { nodes: HTMLElement[]; title: string; focus: HTMLButtonElement } {
  const title = `${o.verb} and lose this guest?`;
  const lead = el('p', 'w21-su-warn');
  lead.append(
    document.createTextNode(`${viewerStore.getState().username || 'This guest'} has `),
    el('b', undefined, listWords(guestLossParts(o.value))),
    document.createTextNode('.'),
  );
  const detail = el(
    'p',
    'w21-su-note',
    `A guest has no password, so there is no signing back in. If you ${o.verb.toLowerCase()}, all of it is lost for good. Register this guest instead and you keep everything.`,
  );
  const keep = el('button', 'w21-btn w21-primary w21-lg w21-block w21-su-cta', 'Register this guest instead') as HTMLButtonElement;
  keep.type = 'button';
  keep.addEventListener('click', () => registerInstead());
  const leaveLabel = `${o.verb} and lose this guest`;
  const leave = el('button', 'w21-btn w21-ghost w21-block w21-su-leave', leaveLabel) as HTMLButtonElement;
  leave.type = 'button';
  leave.addEventListener('click', () => {
    leave.disabled = true;
    keep.disabled = true;
    leave.textContent = o.working;
    void o.leave().then((ok) => {
      if (ok) return;
      leave.disabled = false;
      keep.disabled = false;
      leave.textContent = leaveLabel;
    });
  });
  const foot = el('div', 'w21-su-foot');
  const back = el('button', 'w21-su-later', 'Back') as HTMLButtonElement;
  back.type = 'button';
  back.addEventListener('click', o.back);
  foot.append(back);
  return { nodes: [o.grab, el('h2', 'w21-su-title', title), lead, detail, keep, leave, foot], title, focus: keep };
}

/** Signing out as a guest that holds value (the server said so): the same question, as a sheet of its own. */
function buildSignOutConfirm(value: GuestValue, leave: () => Promise<boolean>): HTMLElement {
  const layer = buildLayer();
  const form = el('form', 'w21-su-sheet w21-su-signin w21-su-confirm') as HTMLFormElement;
  form.setAttribute('role', 'dialog');
  form.setAttribute('aria-modal', 'true');
  form.noValidate = true;
  form.addEventListener('submit', (e) => e.preventDefault());
  const grab = el('i', 'w21-su-grab');
  grab.setAttribute('aria-hidden', 'true');
  const face = leaveFace({ verb: 'Sign out', working: 'Signing out…', value, leave, back: () => dismissSignup(), grab });
  form.setAttribute('aria-label', face.title);
  form.append(...face.nodes);
  layer.append(form);
  requestAnimationFrame(() => face.focus.focus({ preventScroll: true }));
  return layer;
}

/**
 * The other face: a returning player signing back in. Blockari's Auth screen "Sign in" tab, as the
 * same sheet — a username and a password, the one button, and the way over to "Create account".
 * A guest who signs in leaves the guest (and whatever it holds) behind; the sheet says so when
 * there is something held.
 */
function buildSignIn(): HTMLElement {
  const viewer = viewerStore.getState();
  const { chests } = stake();
  const layer = buildLayer();
  const form = el('form', 'w21-su-sheet w21-su-signin') as HTMLFormElement;
  form.setAttribute('role', 'dialog');
  form.setAttribute('aria-modal', 'true');
  form.setAttribute('aria-label', 'Sign in');
  form.noValidate = true;

  const grab = el('i', 'w21-su-grab');
  grab.setAttribute('aria-hidden', 'true');
  // Blockari's /auth card: the logo on top, a Sign in | Create account control, labelled fields.
  const logo = el('img', 'w21-su-logo') as HTMLImageElement;
  logo.src = `${import.meta.env.BASE_URL}logo@1x.webp`;
  logo.alt = '21 Wild';
  logo.draggable = false;
  const seg = el('div', 'w21-seg w21-su-seg');
  seg.setAttribute('role', 'radiogroup');
  seg.setAttribute('aria-label', 'Account');
  seg.style.setProperty('--n', '2');
  seg.style.setProperty('--i', '0');
  const thumb = el('span', 'w21-seg-thumb');
  thumb.setAttribute('aria-hidden', 'true');
  const tabIn = el('button', 'w21-seg-item', 'Sign in') as HTMLButtonElement;
  const tabUp = el('button', 'w21-seg-item', 'Create account') as HTMLButtonElement;
  for (const [t, on] of [[tabIn, true], [tabUp, false]] as const) {
    t.type = 'button';
    t.setAttribute('role', 'radio');
    t.setAttribute('aria-checked', String(on));
    t.tabIndex = on ? 0 : -1;
  }
  tabUp.addEventListener('click', () => switchSheet('signup'));
  seg.append(thumb, tabIn, tabUp);

  const fields = el('div', 'w21-su-fields w21-su-labelled');
  const field = (label: string, input: HTMLInputElement): HTMLElement => {
    const wrap = el('label', 'w21-su-field');
    wrap.append(el('span', 'w21-field-label', label), input);
    return wrap;
  };
  const name = el('input', 'w21-su-input') as HTMLInputElement;
  name.type = 'text';
  name.autocomplete = 'username';
  name.autocapitalize = 'none';
  name.spellcheck = false;
  name.maxLength = 20;
  const pass = el('input', 'w21-su-input w21-su-pass') as HTMLInputElement;
  pass.type = 'password';
  pass.autocomplete = 'current-password';
  pass.addEventListener('focus', () => {
    setTimeout(() => form.querySelector('.w21-su-later')?.scrollIntoView({ block: 'end', behavior: 'smooth' }), 300);
  });
  fields.append(field('Username', name), field('Password', pass));

  const errorNote = el('div', 'w21-note w21-error');
  errorNote.setAttribute('role', 'alert');
  errorNote.hidden = true;
  const showError = (text: string | null): void => {
    errorNote.hidden = text === null;
    errorNote.textContent = text ?? '';
    name.toggleAttribute('aria-invalid', text !== null);
    pass.toggleAttribute('aria-invalid', text !== null);
  };

  // Honest about the trade: this browser's guest is left where it is.
  const warn = el('p', 'w21-su-note');
  warn.hidden = !(viewer.guest && (chests.length > 0 || viewer.xp > 0));
  warn.textContent = chests.length > 0
    ? 'Signing in loses this guest for good, and its locked chests with it. Create an account instead to keep them.'
    : 'Signing in loses this guest for good, with its rank and everything it holds. Create an account instead to keep it.';

  const cta = el('button', 'w21-btn w21-primary w21-lg w21-block w21-su-cta', 'Sign in') as HTMLButtonElement;
  // The Create account control is the tab above; the foot keeps only the one-tap way out.
  const foot = el('div', 'w21-su-foot');
  const later = el('button', 'w21-su-later', 'Later') as HTMLButtonElement;
  later.type = 'button';
  later.addEventListener('click', () => dismissSignup());
  foot.append(later);

  /** The face that asks before a guest with something to keep is left behind (the server said so: 409). */
  const confirmAbandon = (value: GuestValue): void => {
    const restore = (): void => {
      form.classList.remove('w21-su-confirm');
      form.replaceChildren(grab, logo, seg, fields, warn, errorNote, cta, foot);
      name.focus({ preventScroll: true });
    };
    const face = leaveFace({ verb: 'Log in', working: 'Signing in…', value, leave: () => attempt(true), back: restore, grab });
    form.classList.add('w21-su-confirm');
    form.setAttribute('aria-label', face.title);
    form.replaceChildren(...face.nodes);
    face.focus.focus({ preventScroll: true });
  };

  /** One sign-in attempt. True once signed in; false leaves the sheet showing why. */
  const attempt = async (abandon: boolean): Promise<boolean> => {
    const r = await login(name.value.trim(), pass.value, abandon);
    if (r.ok) {
      afterSignIn(r.value.user);
      return true;
    }
    const held = guestValueOf(r);
    if (held) {
      confirmAbandon(held);
      return false;
    }
    // Back on the form, where the error is shown.
    if (form.classList.contains('w21-su-confirm')) {
      form.classList.remove('w21-su-confirm');
      form.replaceChildren(grab, logo, seg, fields, warn, errorNote, cta, foot);
    }
    showError(signInErrorCopy(r.error));
    pass.value = '';
    pass.focus();
    return false;
  };

  let busy = false;
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (busy) return;
    showError(null);
    if (name.value.trim() === '' || pass.value === '') {
      showError('Enter your username and password.');
      (name.value.trim() === '' ? name : pass).focus();
      return;
    }
    busy = true;
    cta.disabled = true;
    cta.textContent = 'Signing in…';
    void attempt(false).then((ok) => {
      if (ok) return;
      busy = false;
      cta.disabled = false;
      cta.textContent = 'Sign in';
    });
  });

  form.append(grab, logo, seg, fields, warn, errorNote, cta, foot);
  layer.append(form);
  return layer;
}

function buildSheet(reason: SignupReason, shownRank: Account['rank'] | null): HTMLElement {
  const viewer = viewerStore.getState();
  const { chests, rarity, days } = stake();
  const guestName = viewer.username;
  // A registered account with no password keeps its own name: nothing to suggest, only a password to set.
  const named = viewer.passwordless && !viewer.guest;

  const layer = buildLayer();
  const form = el('form', 'w21-su-sheet') as HTMLFormElement;
  form.setAttribute('role', 'dialog');
  form.setAttribute('aria-modal', 'true');
  form.setAttribute('aria-labelledby', 'w21-su-title');
  form.noValidate = true;

  const grab = el('i', 'w21-su-grab');
  grab.setAttribute('aria-hidden', 'true');
  const title = el('h2', 'w21-su-title', TITLE[reason](chests.length));
  title.id = 'w21-su-title';

  // The reward is the picture: the locked chest(s), the welcome gift, the rank.
  const reward = el('div', 'w21-su-reward');
  if (chests.length > 0) reward.append(lockedChest({ rarity, count: chests.length, days }));
  // The welcome gift is part of what registering brings: shown, not promised in words.
  reward.append(lockedChest({ rarity: 'common', count: 1, days: null, small: true }));
  const rank = shownRank ?? viewer.rank;
  if (rank) {
    const r = el('div', 'w21-su-rank');
    r.append(rankBadgeCard(rank, 64));
    reward.append(r);
  }

  // The name: a friendly free one fills in as the sheet opens (until the player touches the field);
  // if it cannot be had, the guest's own name stands.
  const fields = el('div', 'w21-su-fields');
  const nameLabel = el('label', 'w21-su-field w21-su-name');
  let av = avatar(guestName);
  const name = el('input', 'w21-su-input') as HTMLInputElement;
  name.type = 'text';
  name.autocomplete = 'username';
  name.autocapitalize = 'none';
  name.spellcheck = false;
  name.maxLength = 20;
  name.placeholder = 'Username';
  name.setAttribute('aria-label', 'Username');
  name.addEventListener('focus', () => name.select());
  nameLabel.append(av, name);
  const pass = el('input', 'w21-su-input w21-su-pass') as HTMLInputElement;
  pass.type = 'password';
  pass.autocomplete = 'new-password';
  pass.placeholder = 'Password';
  pass.setAttribute('aria-label', 'Password');
  pass.addEventListener('focus', () => {
    // A phone's keyboard rises over the foot of the sheet (the layer is fitted to what is left of
    // the screen, and the sheet scrolls inside it): bring the foot up, so the field, the button
    // and Later are all on screen together. A sheet that already fits does not move.
    setTimeout(() => form.querySelector('.w21-su-later')?.scrollIntoView({ block: 'end', behavior: 'smooth' }), 300);
  });
  fields.append(nameLabel, pass);

  let touched = false;
  const keep = el('button', 'w21-su-keep') as HTMLButtonElement;
  keep.type = 'button';
  const paintName = (): void => {
    const shown = name.value || guestName;
    const next = avatar(shown);
    av.replaceWith(next);
    av = next;
    const offer = keepNameOffer(name.value, guestName);
    keep.hidden = offer === null;
    keep.textContent = offer ? `Keep ${offer}` : '';
  };
  name.addEventListener('input', () => {
    touched = true;
    paintName();
  });
  keep.addEventListener('click', () => {
    touched = true;
    name.value = guestName;
    paintName();
  });
  if (named) {
    name.value = guestName;
    paintName();
  } else {
    paintName();
    void suggestName().then((r) => {
      if (!sheet || touched) return;
      name.value = r.ok ? r.value.name : guestName;
      paintName();
    });
  }

  const errorNote = el('div', 'w21-note w21-error');
  errorNote.setAttribute('role', 'alert');
  errorNote.hidden = true;
  const showError = (text: string | null): void => {
    errorNote.hidden = text === null;
    errorNote.textContent = text ?? '';
    name.toggleAttribute('aria-invalid', text !== null);
    pass.toggleAttribute('aria-invalid', text !== null);
  };

  const cta = el('button', 'w21-btn w21-primary w21-lg w21-block w21-su-cta', 'Create account') as HTMLButtonElement;
  const [foot, later] = footRow('Sign in', () => switchSheet('signin'));

  let busy = false;
  let done = false;
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (busy || done) return;
    showError(null);
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(name.value)) {
      showError('Username: 3–20 letters, digits or underscores.');
      return;
    }
    if (pass.value.length < 8) {
      showError('Password: at least 8 characters.');
      pass.focus();
      return;
    }
    busy = true;
    cta.disabled = true;
    cta.textContent = 'Creating…';
    void register(name.value, pass.value).then((r) => {
      if (r.ok) {
        done = true;
        afterRegistration(r.value);
      } else {
        showError(errorCopy(r.error));
        busy = false;
        cta.disabled = false;
        cta.textContent = 'Create account';
      }
    });
  });

  form.append(grab, title, reward, fields, keep, errorNote, cta, foot);
  layer.append(form);
  return layer;
}

function buildCelebration(name: string, chests: readonly ChestSummary[]): HTMLElement {
  const rarity = bestRarity(chests);
  const root = el('div', 'w21-su-celebrate');
  root.setAttribute('role', 'status');
  const stage = el('div', 'w21-su-cel-stage');
  const burst = el('i', 'w21-su-cel-burst');
  burst.setAttribute('aria-hidden', 'true');
  for (let i = 0; i < 12; i++) {
    const b = el('b');
    b.style.setProperty('--a', `${i * 30 + 12}deg`);
    burst.append(b);
  }
  stage.append(burst, lockedChest({ rarity, count: chests.length, days: null, open: true }));
  for (let i = 0; i < 7; i++) {
    const c = coin();
    c.classList.add('w21-su-coin');
    c.setAttribute('aria-hidden', 'true');
    c.style.setProperty('--dx', `${(i - 3) * 30 + (i % 2 ? 10 : -10)}px`);
    c.style.setProperty('--dy', `${-110 - ((i * 37) % 70)}px`);
    c.style.setProperty('--dl', `${120 + ((i * 3) % 7) * 60}ms`);
    stage.append(c);
  }
  const t = el('div', 'w21-su-cel-title');
  const viewer = viewerStore.getState();
  if (viewer.rank) t.append(rankBadgeCard(viewer.rank, 28));
  t.append(el('span', undefined, `Welcome, ${name}`));
  root.append(stage, t);
  return root;
}

/**
 * Mounted once: the sheet, its celebration and the ambient prompt — once a
 * session and once a day, on a calm screen, a beat after it has settled.
 * `screen` is the app's current screen (ui/index.ts calls `notifyScreen`).
 */
export function mountSignupLayer(): { notifyScreen(screen: string): void; notifyStake(): void; destroy(): void } {
  let screen = 'start';
  let timer: ReturnType<typeof setTimeout> | null = null;

  const sync = (): void => {
    const s = signupStore.getState();
    // The other face asked for while one is up: rebuilt, not morphed (the two share nothing but chrome).
    if (s.open && sheet && sheetMode !== s.mode) closeSheet();
    if (s.open && !sheet) {
      sheetMode = s.mode;
      sheet =
        s.mode === 'signout' && s.leave
          ? buildSignOutConfirm(s.leave.value, s.leave.run)
          : s.mode === 'signin'
            ? buildSignIn()
            : buildSheet(s.reason ?? 'prompt', s.rank);
      document.body.append(sheet);
      requestAnimationFrame(() => (sheet?.querySelector('.w21-su-input') as HTMLInputElement | null)?.focus({ preventScroll: true }));
    } else if (!s.open && sheet) {
      closeSheet();
    }
    if (s.celebrating && !celebration) {
      const c = s.celebrating;
      celebration = buildCelebration(c.name, c.chests);
      document.body.append(celebration);
      playChestBeat('collect');
      // (`localStorage.w21.celebrateMs` lengthens it for the capture: a still takes seconds under software GL.)
      const hold = reducedMotion() ? 700 : (Number(globalThis.localStorage?.getItem('w21.celebrateMs')) || CELEBRATE_MS);
      // The arrival rises under the celebration's own veil; the veil then fades to it.
      setTimeout(() => {
        arriveAfterCelebration(c.chests);
        celebration?.classList.add('leaving');
      }, hold);
      setTimeout(() => {
        celebration?.remove();
        celebration = null;
        finishCelebration();
      }, hold + 420);
    }
    schedule();
  };

  const schedule = (): void => {
    if (timer) clearTimeout(timer);
    timer = null;
    const v = viewerStore.getState();
    const chests = chestStore.getState();
    const due = promptDue({
      // A guest, or a registered account with no password: both have something to keep.
      guest: v.guest || v.passwordless,
      screen,
      arrivalOpen: arrivalStore.getState().batch !== null,
      sheetOpen: signupStore.getState().open || signupStore.getState().celebrating !== null,
      hasStake: v.xp > 0 || chests.unopened.length + chests.held.length > 0,
    });
    if (!due) return;
    timer = setTimeout(() => {
      timer = null;
      if (canPrompt()) openSignup('prompt');
    }, PROMPT_DELAY_MS);
  };

  const off = [signupStore.subscribe(sync), arrivalStore.subscribe(schedule), chestStore.subscribe(schedule), viewerStore.subscribe(schedule)];
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && signupStore.getState().open) dismissSignup();
  };
  window.addEventListener('keydown', onKey);
  return {
    notifyScreen(next) {
      screen = next;
      schedule();
    },
    notifyStake: schedule,
    destroy() {
      for (const f of off) f();
      window.removeEventListener('keydown', onKey);
      if (timer) clearTimeout(timer);
      closeSheet();
      celebration?.remove();
      celebration = null;
    },
  };
}
